import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

// SE-1723: the Sys-Admin "bulk end memberships" selection tray. Unlike the
// DataGrid's own row selection (which resets on page/sort/filter change), this
// tray is persistent app state, so a Sys Admin can gather users across many
// searches and then end their memberships together.

export type BulkEndTrayMembership = {
  id: string;
  programId: string | null;
  programName?: string | null;
  programRole?: string | null;
};

export type BulkEndTrayEntry = {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  // The user's ACTIVE memberships at add time — candidates to end in the review.
  memberships: BulkEndTrayMembership[];
};

type BulkEndTraySlice = {
  // The tray BELONGS to one logged-in user. ownerId pins it so it can be
  // persisted/restored per-login and NEVER transfers to a different account.
  ownerId: string | null;
  entries: BulkEndTrayEntry[];
};

const initialState: BulkEndTraySlice = {
  ownerId: null,
  entries: []
};

export const bulkEndTraySlice = createSlice({
  name: "bulkEndTray",
  initialState,
  reducers: {
    // Login / hydrate: set the owner and load that user's saved tray. Logout =
    // setTrayOwner({ ownerId: null, entries: [] }).
    setTrayOwner: (
      state,
      action: PayloadAction<{ ownerId: string | null; entries: BulkEndTrayEntry[] }>
    ) => {
      state.ownerId = action.payload.ownerId;
      state.entries = action.payload.entries;
    },
    addToTray: (state, action: PayloadAction<BulkEndTrayEntry>) => {
      if (!state.entries.some((e) => e.userId === action.payload.userId)) {
        state.entries.push(action.payload);
      }
    },
    removeFromTray: (state, action: PayloadAction<string>) => {
      state.entries = state.entries.filter((e) => e.userId !== action.payload);
    },
    toggleTray: (state, action: PayloadAction<BulkEndTrayEntry>) => {
      const i = state.entries.findIndex(
        (e) => e.userId === action.payload.userId
      );
      if (i >= 0) state.entries.splice(i, 1);
      else state.entries.push(action.payload);
    },
    clearTray: (state) => {
      state.entries = [];
    }
  }
});

export const { setTrayOwner, addToTray, removeFromTray, toggleTray, clearTray } =
  bulkEndTraySlice.actions;

export const selectTrayOwnerId = (state: RootState) => state.bulkEndTray.ownerId;
export const selectTrayEntries = (state: RootState) =>
  state.bulkEndTray.entries;
export const selectTrayCount = (state: RootState) =>
  state.bulkEndTray.entries.length;
export const selectIsInTray = (userId: string) => (state: RootState) =>
  state.bulkEndTray.entries.some((e) => e.userId === userId);

export default bulkEndTraySlice.reducer;

// ---- per-user localStorage persistence --------------------------------------
// Keyed by the owner's user id so a different login reads a DIFFERENT (empty)
// tray and one user's tray can never surface under another account.

const STORAGE_PREFIX = "bulkEndTray:";
const keyFor = (ownerId: string) => `${STORAGE_PREFIX}${ownerId}`;

export function loadTrayEntries(ownerId: string): BulkEndTrayEntry[] {
  try {
    const raw = window.localStorage.getItem(keyFor(ownerId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as BulkEndTrayEntry[]) : [];
  } catch {
    return [];
  }
}

export function saveTrayEntries(
  ownerId: string,
  entries: BulkEndTrayEntry[]
): void {
  try {
    window.localStorage.setItem(keyFor(ownerId), JSON.stringify(entries));
  } catch {
    // storage full / unavailable — the tray still works for this session.
  }
}
